import { useLocation } from 'react-router';

import { Search } from 'lucide-react';

import { useCurrentUser } from '@/features/auth';
import { ThemeToggle } from '@/lib/ui';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/routes';
import { useAppDispatch } from '@/store/hooks';
import { openCommandPalette } from '@/store/uiSlice';

import styles from './Topbar.module.scss';

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

const ROUTE_TITLES: Record<string, string> = {
  [ROUTES.dashboard]: 'Dashboard',
  [ROUTES.apiKeys]: 'API Keys',
  [ROUTES.analytics]: 'Analytics',
  [ROUTES.billing]: 'Billing',
  [ROUTES.webhooks]: 'Webhooks',
  [ROUTES.webhookLogs]: 'Webhook Logs',
  [ROUTES.activity]: 'Activity',
  [ROUTES.settings]: 'Settings - Security',
  [ROUTES.settingsOrganization]: 'Settings - Organization',
  [ROUTES.allowedHosts]: 'Allowed Hosts',
  [ROUTES.mcpSessions]: 'MCP Sessions',
  [ROUTES.modelCards]: 'Model Cards',
  [ROUTES.modelCardDetail]: 'Model Card Details',
  [ROUTES.howItWorks]: 'How It Works',
  [ROUTES.admin]: 'Admin',
  [ROUTES.adminUsers]: 'Admin — Users',
  [ROUTES.adminApiKeys]: 'Admin — API Keys',
  [ROUTES.adminApiUsage]: 'Admin — API Usage',
  [ROUTES.adminDetections]: 'Admin — Detections',
  [ROUTES.adminAllowedHosts]: 'Admin — Allowed Hosts',
  [ROUTES.adminThresholds]: 'Admin — Thresholds',
};

export function Topbar() {
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const dispatch = useAppDispatch();

  const title =
    ROUTE_TITLES[pathname] ??
    (pathname.startsWith(`${ROUTES.modelCards}/`) ? 'Model Card Details' : '');

  return (
    <header className={styles.topbar}>
      <span className={cn(styles.title, 'min-w-0 flex-1 truncate')}>{title}</span>
      <div className="flex shrink-0 items-center gap-3">
        {user && (
          <div className="min-w-0 max-w-[120px] text-right leading-tight sm:max-w-[220px]">
            <p className="truncate text-sm font-semibold">
              {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
            </p>
            <p className="truncate text-xs text-muted-foreground">{user.email || '-'}</p>
          </div>
        )}

        <button
          type="button"
          onClick={() => dispatch(openCommandPalette())}
          aria-label="Open command palette"
          className="hidden shrink-0 items-center gap-2 rounded-lg border border-[var(--color-glass-border)] bg-[var(--color-glass-bg)] px-3 py-1.5 text-sm text-muted-foreground shadow-sm backdrop-blur-[3px] transition-colors hover:text-foreground md:flex md:w-52 lg:w-64"
        >
          <Search className="size-4 shrink-0" aria-hidden />
          <span className="flex-1 truncate text-left">Search…</span>
          <kbd className="shrink-0 rounded border border-[var(--color-glass-border)] px-1.5 py-0.5 font-mono text-xs">
            {isMac ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>

        <ThemeToggle />
      </div>
    </header>
  );
}
